import { IEdge, IEnumerable, IGraph, INode, List, Neighborhood, TraversalDirection } from '@yfiles/yfiles';

/**
 * Gets all nodes connected to the given node in a flow, both predecessors and successors.
 * @param flowNode The starting node to trace the flow from
 * @param graph The graph to search in
 * @param products Set of product names to filter by
 * @returns An enumerable of nodes that are part of the same flow
 */
export function getFlowNodes(
  flowNode: INode | null,
  graph: IGraph,
  products: Set<string>
): IEnumerable<INode> {
  if (flowNode === null) {
    return new List();
  }

  const startNodes = graph.isGroupNode(flowNode) ? graph.getChildren(flowNode) : flowNode;

  const filter = (edge: IEdge) => edge?.tag?.product !== undefined && products.has(edge.tag.product);

  return new Neighborhood({
    startNodes: startNodes,
    traversalDirection: TraversalDirection.SUCCESSOR,
    subgraphEdges: {
      includes: filter
    }
  })
    .run(graph)
    .neighbors.concat(
      new Neighborhood({
        startNodes: startNodes,
        traversalDirection: TraversalDirection.PREDECESSOR,
        subgraphEdges: {
          includes: filter
        }
      }).run(graph).neighbors
    )
    .concat(startNodes);
}

/**
 * Collects all edges and nodes that are part of a flow, filtering by products.
 * @param flowNodesList The list of nodes in the flow
 * @param graph The graph to search in
 * @param products Set of product names to filter by
 * @param flowEdges Output set to collect edges that are part of the flow
 * @param flowNodes Output set to collect nodes that are part of the flow
 */
export function getFlowItems(
  flowNodesList: IEnumerable<INode>,
  graph: IGraph,
  products: Set<string>,
  flowEdges: Set<IEdge>,
  flowNodes: Set<INode>
) {
  flowNodesList.forEach((node) => {
    graph
      .edgesAt(node)
      .filter(
        (edge) =>
          flowNodesList.includes(edge.sourceNode!) &&
          flowNodesList.includes(edge.targetNode!) &&
          edge.tag &&
          products.has(edge.tag.product)
      )
      .forEach((edge) => {
        flowEdges.add(edge);
        flowNodes.add(edge.sourceNode!);
        flowNodes.add(edge.targetNode!);
      });
  });
  
  // Add parent nodes (groups) to ensure they're visible
  [...flowNodes].forEach((node) => {
    const parent = graph.getParent(node);
    if (parent) {
      flowNodes.add(parent);
    }
  });
}

/**
 * Gets all products that pass through a node.
 * @param graph The graph to search in
 * @param node The node to check
 * @returns A set of product names
 */
export function getProductsForNode(graph: IGraph, node: INode): Set<string> {
  const products: Set<string> = new Set();
  
  // Check edges directly connected to this node
  graph.edgesAt(node).forEach((edge) => {
    if (edge.tag && edge.tag.product) {
      products.add(edge.tag.product);
    }
  });
  
  // If it's a group node, check edges connected to children
  if (graph.isGroupNode(node)) {
    graph.getChildren(node).forEach((childNode) => {
      graph.edgesAt(childNode).forEach((edge) => {
        if (edge.tag && edge.tag.product) {
          products.add(edge.tag.product);
        }
      });
    });
  }

  return products;
}
